export type Money = {
  /** ISO 4217 currency code, e.g. "USD". */
  currency: string;
  /** The amount as a whole number, in units of `scale`: 5000 for $50.00, or 5850 at scale 4 for $0.585. */
  amount: number;
  /**
   * How many decimals `amount` counts in: 5850 at scale 4 is 0.5850. Left out, it is the currency's own (2 for USD),
   * so `amount` is in the currency's smallest unit.
   */
  scale?: number;
};

/** How to show an amount. */
export type FormatMoneyOptions = {
  /** The locale to format in; defaults to the reader's. */
  locale?: string;
  /**
   * How to show the currency.
   * @default 'symbol'
   */
  display?: 'symbol' | 'code';
  /**
   * Show the amount as a whole number, without its decimals.
   * @default false
   */
  whole?: boolean;
  /**
   * How to round when showing fewer decimals than the amount has.
   * @default 'halfExpand'
   */
  roundingMode?: Intl.NumberFormatOptions['roundingMode'];
  /**
   * Abbreviate large amounts, e.g. "£73.5m" for 73,500,000.00: at most one decimal, or none with `whole`. Ignores a
   * finer `scale`, being only for reading.
   * @default false
   */
  compact?: boolean;
};

/** The number of decimals a currency has, e.g. 2 for USD, 0 for JPY, as the locale's number formatting has it. */
export function getCurrencyDecimals(currency: string, locale?: string): number {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 0;
}

/** The decimals an amount counts in: its `scale`, or the currency's own. */
function getScale({ currency, scale }: Money, locale?: string): number {
  return scale ?? getCurrencyDecimals(currency, locale);
}

/**
 * Formats an amount for reading, e.g. "$50.00" or "USD 50.00": with the currency's decimals, and with more when its
 * `scale` has them and they are not zero, so $0.585 at scale 4 reads "$0.585" and $0.65 reads "$0.65". With `compact`,
 * large amounts are abbreviated to at most one decimal, so 73,500,000.00 reads "£73.5m".
 */
export function formatMoney(money: Money, options: FormatMoneyOptions = {}): string {
  const { locale, display = 'symbol', whole = false, roundingMode, compact = false } = options;
  const digits = getCurrencyDecimals(money.currency, locale);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: money.currency,
    currencyDisplay: display,
    minimumFractionDigits: whole || compact ? 0 : digits,
    maximumFractionDigits: whole ? 0 : compact ? 1 : Math.max(digits, getScale(money, locale)),
    roundingMode,
    ...(compact ? { notation: 'compact' } : {}),
  }).format(fromSmallestUnit({ ...money, locale }));
}

/** The separators a locale writes numbers with, e.g. "," and "." in English; "," and "." when it has none. */
export function getNumberSeparators(locale?: string): { group: string; decimal: string } {
  const parts = new Intl.NumberFormat(locale, { style: 'decimal' }).formatToParts(12_345.6);
  return {
    group: parts.find((part) => part.type === 'group')?.value ?? ',',
    decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
  };
}

/**
 * A value as people write it (50.00) as a whole number of the currency's smallest unit (5000 for USD), or of the
 * finer unit `scale` gives (0.585 at scale 4 is 5850). It rounds half away from zero, as the value is written: the
 * product is first cut to 15 significant digits, so 1.005 gives 101 rather than the 100 floating point would.
 */
export function toSmallestUnit({
  value,
  currency,
  locale,
  scale = getCurrencyDecimals(currency, locale),
}: {
  value: number;
  currency: string;
  locale?: string;
  scale?: number;
}) {
  const exact = Number((Math.abs(value) * 10 ** scale).toPrecision(15));
  return Math.sign(value) * Math.round(exact);
}

/** An amount as people write it (50.00), from the currency's smallest unit (5000 for USD) or the amount's `scale`. */
export function fromSmallestUnit({ locale, ...money }: Money & { locale?: string }) {
  return money.amount / 10 ** getScale(money, locale);
}

/**
 * An amount in other decimals, e.g. a rate times a quantity brought back to cents to charge: rounded half away from
 * zero when there are fewer. `scale` defaults to the currency's, and is always set on the result.
 */
export function rescale(money: Money, scale?: number, locale?: string): Money & { scale: number } {
  const to = scale ?? getCurrencyDecimals(money.currency, locale);
  const from = getScale(money, locale);
  if (to >= from) return { ...money, amount: money.amount * 10 ** (to - from), scale: to };

  // Whole numbers only, so the rounding is exact.
  const factor = 10 ** (from - to);
  const remainder = money.amount % factor;
  const quotient = (money.amount - remainder) / factor;
  const away = Math.abs(remainder) * 2 >= factor ? Math.sign(remainder) : 0;
  return { ...money, amount: quotient + away || 0, scale: to };
}
